import { useEffect, useRef, useState } from "react";
import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  useReducedMotion,
} from "framer-motion";

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [travelY, setTravelY] = useState(() => -(window.innerHeight / 2 - 36));

  useEffect(() => {
    const onResize = () => setTravelY(-(window.innerHeight / 2 - 36));
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  // Title: holds at full opacity for the first ~200px of scroll, THEN
  // scales up and fades. Flat-head 3-point ranges: explicit value at
  // progress 0 (framer 12 quirk: 2-point transforms misbehave out of range).
  const titleScale = useTransform(scrollYProgress, [0, 0.14, 0.5], [1, 1, 1.75]);
  const titleOpacity = useTransform(scrollYProgress, [0, 0.14, 0.48], [1, 1, 0]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.14, 0.48], [1, 1, 0]);

  // Logo: shrinks and travels up into the fixed header (deferred start)
  const logoScale = useTransform(scrollYProgress, [0, 0.14, 0.6], [1, 1, 0.22]);
  const logoY = useTransform(scrollYProgress, [0, 0.14, 0.6], [0, 0, travelY]);
  const logoOpacity = useTransform(scrollYProgress, [0.6, 0.72], [1, 0]);

  // 3D tilt driven by cursor
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const smx = useSpring(mx, { stiffness: 80, damping: 18 });
  const smy = useSpring(my, { stiffness: 80, damping: 18 });
  const rotateY = useTransform(smx, [0, 1], [-30, 30]);
  const rotateX = useTransform(smy, [0, 1], [22, -22]);

  const onMouseMove = (e) => {
    mx.set(e.clientX / window.innerWidth);
    my.set(e.clientY / window.innerHeight);
  };

  return (
    <section
      ref={ref}
      onMouseMove={reduce ? undefined : onMouseMove}
      data-testid="hero-section"
      className={reduce ? "relative" : "relative h-[180vh]"}
    >
      <div
        className={`${
          reduce ? "relative" : "sticky top-0"
        } flex h-screen flex-col items-center justify-center px-5`}
      >
        {/* Floating logo with 3D tilt */}
        <motion.div
          style={reduce ? undefined : { y: logoY, scale: logoScale, opacity: logoOpacity }}
          className="relative z-10 mb-8 md:mb-10"
        >
          <div style={{ perspective: 900 }}>
            <motion.img
              src="/assets/servin-labs-logo.png"
              alt="Servin Labs"
              data-testid="hero-logo"
              style={reduce ? undefined : { rotateX, rotateY, transformStyle: "preserve-3d" }}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
              className="h-28 w-28 object-contain drop-shadow-[0_0_16px_rgba(255,255,255,0.3)] md:h-40 md:w-40"
            />
          </div>
        </motion.div>

        {/* Giant title */}
        <motion.h1
          data-testid="hero-title"
          style={
            reduce
              ? { fontSize: "clamp(2.75rem, 8.5vw, 9rem)" }
              : {
                  scale: titleScale,
                  opacity: titleOpacity,
                  fontSize: "clamp(2.75rem, 8.5vw, 9rem)",
                }
          }
          className="text-center font-display font-bold leading-[0.95] tracking-tight text-white"
        >
          <motion.span
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="block"
          >
            Architecture of
          </motion.span>
          <motion.span
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.35, ease: [0.22, 1, 0.36, 1] }}
            className="block"
          >
            Digital Empires<span className="text-white/40">.</span>
          </motion.span>
        </motion.h1>

        {/* Spanish support copy */}
        <motion.p
          style={reduce ? undefined : { opacity: copyOpacity }}
          data-testid="hero-subcopy"
          className="mt-8 max-w-md text-center text-sm text-white/50 md:mt-10 md:text-base"
        >
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: [0.22, 1, 0.36, 1] }}
            className="block"
          >
            Diseñamos y construimos ecosistemas digitales de lujo para marcas
            que dominan su categoría.
          </motion.span>
        </motion.p>

        {/* Scroll indicator */}
        <motion.div
          style={reduce ? undefined : { opacity: copyOpacity }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2, duration: 1 }}
          data-testid="scroll-indicator"
          className="absolute bottom-8 hidden flex-col items-center gap-3 [@media(min-height:760px)]:flex"
        >
          <span className="text-[10px] tracking-[0.4em] text-white/35">
            SCROLL
          </span>
          <div className="relative h-12 w-px overflow-hidden bg-white/10">
            <motion.div
              animate={{ y: ["-100%", "100%"] }}
              transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
              className="absolute h-1/2 w-full bg-[#0047FF]"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
